import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { NkpSor, NevsorTag, nkpBeolvas, nkpParosit, jegyHatarral } from '../../util/nkp-eredmeny';

/**
 *  NKP-EREDMÉNYEK BEILLESZTÉSE a tömeges jegybeíráshoz.
 *
 *  A tanár bemásolja az NKP „Legjobb eredmény" listáját (a két névsort – időkedvezményesek
 *  is – egyben). A rendszer név (és ha egyezik, e-mail) alapján párosít a kiválasztott osztály /
 *  csoport névsorához, és ELŐNÉZETET mutat: ki mit kap, kit nem talált, kit kétszer. A kétes
 *  sort a tanár választja ki. Az ablak NEM ment: a kiválasztott értékeket a névsor mezőibe
 *  írja, a tanár átnézi, és a megszokott Mentés gombbal rögzít – így minden meglévő
 *  ellenőrzés (téma, tanév-zár, dupla mentés) érvényes marad.
 */
@Component({
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule],
  selector: 'app-nkp-beillesztes',
  templateUrl: './nkp-beillesztes.component.html',
  styleUrls: ['./nkp-beillesztes.component.scss'],
})
export class NkpBeillesztesComponent {
  @Input() nevsor: NevsorTag[] = [];
  /** '1' = osztályzat (1–5), '2' = százalék – a képernyő osztályozási módja. */
  @Input() mod: '1' | '2' = '2';
  /** A tantárgy már más formában kapott jegyet: a mód nem változtatható. */
  @Input() modZarolt = false;

  szoveg = '';
  sorok: (NkpSor & { oktazon: string; mod: string | null; jeloltek: number })[] = [];
  hibak: { sor: number; szoveg: string }[] = [];
  feldolgozva = false;
  nemIrtaJelol = true;
  hatarok: [number, number, number, number] = [40, 55, 70, 85];

  constructor(private modal: ModalController) {
    try {
      const h = JSON.parse(localStorage.getItem('nkp_ponthatarok') || 'null');
      if (Array.isArray(h) && h.length === 4 && h.every((x) => Number.isFinite(Number(x)))) this.hatarok = h.map(Number) as any;
    } catch (e) { /* alapértékek */ }
  }

  feldolgoz() {
    const r = nkpBeolvas(this.szoveg);
    this.hibak = r.hibak;
    this.sorok = r.sorok.map((s) => {
      const p = nkpParosit(s, this.nevsor);
      return { ...s, oktazon: p.tag?.oktazon || '', mod: p.mod, jeloltek: p.jeloltek.length };
    });
    this.feldolgozva = true;
  }

  /** A beírandó érték egy sorhoz (üres = nem írunk be semmit). */
  ertek(s: NkpSor): string {
    if (s.allapot !== 'eredmeny') return this.nemIrtaJelol ? '–' : '';
    if (s.szazalek === null) return '';
    //  Százalék vagy jegy – a százalék egészre kerekítve (a mentés is egészre számol).
    if (this.mod === '2') return String(Math.round(Number(s.szazalek)));
    return String(jegyHatarral(s.szazalek, this.hatarok));
  }

  eredmenySzoveg(s: NkpSor): string {
    if (s.allapot === 'nem_kezdett') return 'Nem kezdett hozzá';
    if (s.allapot === 'nem_adta_be') return 'Még nem adta be';
    return `${String(s.szazalek).replace('.', ',')}%${s.pont !== null ? ` (${String(s.pont).replace('.', ',')} pont)` : ''}`;
  }

  /** Két NKP-sor ugyanahhoz a tanulóhoz – a tanárnak kell eldöntenie, melyik. */
  get duplak(): Set<string> {
    const db = new Map<string, number>();
    for (const s of this.sorok) if (s.oktazon) db.set(s.oktazon, (db.get(s.oktazon) || 0) + 1);
    return new Set([...db.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }

  get parositatlan(): number { return this.sorok.filter((s) => !s.oktazon).length; }

  /** A névsor tanulói, akik a listában NEM szerepelnek – nekik nem írunk semmit. */
  get kimaradtTanulok(): NevsorTag[] {
    const van = new Set(this.sorok.map((s) => s.oktazon).filter(Boolean));
    return this.nevsor.filter((t) => !van.has(t.oktazon));
  }

  get kimaradtNevek(): string { return this.kimaradtTanulok.map((t) => t.nev).join(', '); }

  get beirhato(): number {
    const d = this.duplak;
    return this.sorok.filter((s) => s.oktazon && !d.has(s.oktazon) && this.ertek(s) !== '').length;
  }

  hatarValt() {
    try { localStorage.setItem('nkp_ponthatarok', JSON.stringify(this.hatarok)); } catch (e) { /* nem kritikus */ }
  }

  mehet() {
    const d = this.duplak;
    const ertekek: { [oktazon: string]: string } = {};
    for (const s of this.sorok) {
      if (!s.oktazon || d.has(s.oktazon)) continue;
      const e = this.ertek(s);
      if (e !== '') ertekek[s.oktazon] = e;
    }
    this.modal.dismiss({ mod: this.mod, ertekek }, 'ok');
  }

  megse() { this.modal.dismiss(null, 'cancel'); }
}
